import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { icons } from "../../../svg";
import { CustomAlert } from "../../../code-connect/component/CustomAlert";
import { CustomButton } from "../../../code-connect/component/CustomButton";
import { CustomTypography } from "../../../code-connect/component/CustomTypography";
import "./productNotAvailablePanel.css";

interface ProductNotAvailablePanelProps {
  open: boolean;
  productName?: string;
  onCancel: () => void;
  onAddProduct: () => void;
}

const ProductNotAvailablePanel: React.FC<ProductNotAvailablePanelProps> = ({
  open,
  productName,
  onCancel,
  onAddProduct,
}) => {
  const overlayRootRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") return;

    let root = document.getElementById("mobile-overlay-root");
    if (!root) {
      root = document.createElement("div");
      root.id = "mobile-overlay-root";
      root.style.position = "fixed";
      root.style.top = "0";
      root.style.left = "0";
      root.style.right = "0";
      root.style.bottom = "0";
      root.style.zIndex = "100000";
      root.style.pointerEvents = "none";
      document.body.appendChild(root);
    }

    overlayRootRef.current = root as HTMLElement;
  }, []);

  const formattedProductName = useMemo(() => {
    if (!productName || !productName.trim()) return "This product";
    const trimmed = productName.trim().toLowerCase();
    return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
  }, [productName]);

  if (!open) return null;

  const panel = (
    <div
      className="product-not-available-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onCancel();
        }
      }}
      role="presentation"
    >
      <div
        className="product-not-available-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Product not available"
      >
        <div className="product-not-available-container">
          <CustomTypography
            variant="h6"
            customFontWeight={500}
            customColor="var(--text-primary, #475569)"
            customFontSize="var(--_fontSize-typography-h6, 20px)"
            className="product-not-available-title"
          >
            Product Not Available
          </CustomTypography>

          <CustomAlert
            severity="warning"
            icon={
              <img
                src={icons.ExclamationMarkTriangle}
                alt="warning icon"
                className="product-not-available-alert-icon"
              />
            }
            className="product-not-available-alert"
            role="alert"
          >
            <div className="product-not-available-alert-text">
              <CustomTypography
                customColor="var(--_components-alert-warning-color, #663C00)"
                customFontSize="var(--_fontSize-1rem, 16px)"
                customFontWeight={500}
                className="product-not-available-alert-title"
              >
                {formattedProductName} is not added to this origin
              </CustomTypography>
              <CustomTypography
                customColor="var(--_components-alert-warning-color, #663C00)"
                customFontSize="12px"
                customFontWeight={400}
                className="product-not-available-alert-subtitle"
              >
                Would you like to add this product to this origin?
              </CustomTypography>
            </div>
          </CustomAlert>

          <div className="product-not-available-actions">
            <CustomButton
              variant="text"
              onClick={onCancel}
              customColor="var(--secondary-main, #64748B)"
              customFontSize="14px"
              className="product-not-available-cancel"
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={onAddProduct}
              customColor="var(--primary-contrastText, #FFF)"
              customFontSize="14px"
              className="product-not-available-add"
            >
              <span className="product-not-available-add-plus">+</span>
              Add Product
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  );

  return overlayRootRef.current
    ? createPortal(panel, overlayRootRef.current)
    : panel;
};

export default ProductNotAvailablePanel;
